import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './EditLoan.css';
import PageTitle from '../../components/UI/PageTitle';
import { getLoanById, editLoan } from '../../api/Index';

const EditLoan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loanData, setLoanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    notes: '',
    status: ''
  });

  const [newRepayment, setNewRepayment] = useState({
    amount: '',
    date: '',
    receiptNumber: ''
  });

  const loanStatuses = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'disbursed', label: 'Disbursed' },
    { value: 'paid', label: 'Paid' },
    { value: 'defaulted', label: 'Defaulted' }
  ];

  // Fetch loan data on component mount
  useEffect(() => {
    const fetchLoanData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching loan data for ID:', id);
        const response = await getLoanById(id);
        console.log('Loan data response:', response);
        const loan = response.data;
        setLoanData(loan);
        setFormData({
          notes: loan.notes || '',
          status: loan.status || ''
        });
      } catch (err) {
        setError(err.message || 'Failed to fetch loan data');
        console.error('Error fetching loan:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLoanData();
    } else {
      setError('No loan ID provided');
      setLoading(false);
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRepaymentChange = (e) => {
    const { name, value } = e.target;
    setNewRepayment(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || '' : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await editLoan(id, formData);
      alert('Loan updated successfully!');
      navigate(`/loan-details?id=${id}`);
    } catch (err) {
      alert(`Error updating loan: ${err.message}`);
      console.error('Error updating loan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRepayment = async (e) => {
    e.preventDefault();
    if (!newRepayment.amount || !newRepayment.date) {
      alert('Please fill in amount and date');
      return;
    }
    
    try {
      setLoading(true);
      await editLoan(id, { newRepayment });
      
      // Refresh loan data
      const response = await getLoanById(id);
      setLoanData(response.data);
      
      // Reset form
      setNewRepayment({ amount: '', date: '', receiptNumber: '' });
      alert('Repayment added successfully!');
    } catch (err) {
      alert(`Error adding repayment: ${err.message}`);
      console.error('Error adding repayment:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title='Edit Loan' />
        <div>Loading loan data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title='Edit Loan' />
        <div style={{color: 'red'}}>Error: {error}</div>
      </div>
    );
  }

  if (!loanData) {
    return (
      <div>
        <PageTitle title='Edit Loan' />
        <div>Loan not found.</div>
      </div>
    );
  }

  // Calculate totals from repayments array
  const totalRepayments = loanData.totalRepayments || 0;
  const remainingAmount = loanData.remainingAmount || 0;

  return (
    <div>
      <PageTitle title='Edit Loan' />
      <div>
        <div className="form-container">
          {/* Loan Info Display */}
          <div className="loan-info">
            <h3>Loan Information</h3>
            <p><strong>Borrower:</strong> {loanData.user?.name || 'Unknown'}</p>
            <p><strong>Loan Amount:</strong> KES {(loanData.amount || 0).toLocaleString()}</p>
            <p><strong>Total Amount (with interest):</strong> KES {(loanData.totalAmount || 0).toLocaleString()}</p>
            <p><strong>Total Repayments:</strong> KES {totalRepayments.toLocaleString()}</p>
            <p><strong>Remaining Amount:</strong> KES {remainingAmount.toLocaleString()}</p>
          </div>

          <form className="edit-loan-form" onSubmit={handleSubmit}>
            <h3>Editable Fields</h3>

            <div className="input-group">
              <label className="input-label">Notes</label>
              <textarea
                name="notes"
                className="text-area"
                placeholder="Enter loan notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
              />
            </div>

            <div className="input-group">
              <label className="input-label">Status</label>
              <select
                name="status"
                className="input"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                {loanStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit" className='form-button'>Update Loan</button>
          </form>

          {/* Existing Repayments */}
          <div className="repayments-section">
            <h3>Loan Repayments</h3>
            {loanData.repayments && loanData.repayments.length > 0 ? (
              <table className="repayments-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (KES)</th>
                    <th>Receipt Number</th>
                  </tr>
                </thead>
                <tbody>
                  {loanData.repayments.map((repayment, index) => (
                    <tr key={index}>
                      <td>{new Date(repayment.date).toLocaleDateString()}</td>
                      <td>{(repayment.amount || 0).toLocaleString()}</td>
                      <td>{repayment.receiptNumber || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No repayments recorded yet.</p>
            )}
          </div>

          {/* Add New Repayment */}
          <form className="edit-loan-form" onSubmit={handleAddRepayment}>
            <h3>Add New Repayment</h3>

            <div className="input-group">
              <label className="input-label">Amount (KES)</label>
              <input
                type="number"
                name="amount"
                className="input"
                placeholder="Enter repayment amount"
                value={newRepayment.amount}
                onChange={handleRepaymentChange}
                min="0"
                step="0.01"
                required
              />
            </div>

            <div className="input-group">
              <label className="input-label">Date</label>
              <input
                type="date"
                name="date"
                className="input"
                value={newRepayment.date}
                onChange={handleRepaymentChange}
                required
              />
            </div>

            <div className="input-group">
              <label className="input-label">Receipt Number (Optional)</label>
              <input
                type="text"
                name="receiptNumber"
                className="input"
                placeholder="Enter receipt number"
                value={newRepayment.receiptNumber}
                onChange={handleRepaymentChange}
              />
            </div>

            <button type="submit" className='form-button'>Add Repayment</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditLoan;